
// CSV output-profile shape + the one built-in "Standard PointView" profile
// that represents the pre-this-task, fixed CSV output (task: "Den
// eksisterende CSV-eksport skal så vidt muligt videreføres som en
// standardprofil"). A profile is a plain JSON-serialisable object (see
// CsvProfileStore.js for persistence) - deliberately no functions/classes
// on it, so it can round-trip through localStorage untouched.
//
// column.source is one of the source shapes documented in dataSources.js.
// column.header is the CSV column header text the user chose - NEVER the
// same thing as the source id (task: "Det er vigtigt, at header-navnet og
// datakilden er to forskellige ting").

import {FEATURE_FIELDS, GEOMETRY_FIELDS} from "./dataSources.js";

import {t} from "../../i18n/index.js";
let counter = 0;
export function generateColumnId(){
	counter++;
	return `col_${Date.now().toString(36)}_${counter}`;
}
export function generateProfileId(){
	counter++;
	return `profile_${Date.now().toString(36)}_${counter}`;
}

function col(header, source, extra = {}){
	return {id: generateColumnId(), header, source, fallback: "", ...extra};
}

// The standard profile's own "Vertex" column together with X/Y/Z: matches
// the pre-existing CSVFeatureExporter's one-row-per-vertex output exactly
// (task: "Standard PointView-profilen skal reproducere den eksisterende
// forventede CSV-adfærd"). "Attributter" is the {kind:"attributes-all"}
// pseudo-column (see csvGenerator.js's expandColumns()) that reproduces the
// old exporter's dynamic "one column per attribute key actually present
// across the exported features" behaviour, as a first-class, reusable
// column kind rather than a standard-profile-only special case.
// Brugerens definition (2026-10): Standard PointView = ID (fortløbende nummer), X, Y, Z, Name, Vertex - én række pr. vertex.
// (Tidligere: FeatureID, Name, GeometryType, Vertex, X, Y, Z, Category, Tool + dynamiske attributter.) Alt det øvrige findes
// stadig som datakilder, og en ny profil starter med dem alle (createFullProfile).
export function createStandardProfile(){
	return {
		id: "standard",
		name: t("Standard PointView"),
		outputModel: "vertex",
		separator: ",",
		decimalSeparator: ".",
		encoding: "utf8",
		header: true,
		coordinatePrecision: null,
		columns: [
			col("ID", {kind: "rownumber"}),
			col("X", {kind: "geometry", field: "x"}),
			col("Y", {kind: "geometry", field: "y"}),
			col("Z", {kind: "geometry", field: "z"}),
			col("Name", {kind: "feature", field: "name"}),
			col("Vertex", {kind: "geometry", field: "vertexIndex"}),
		],
	};
}

// En NY profil er "født med alle muligheder" (bruger-krav): først en ID-kolonne med fortløbende nummer, så alle
// feature-felter, alle geometri-felter og de dynamiske attributter - brugeren sletter de kolonner, der ikke skal med.
// Én række pr. vertex, så vertex-felterne (Vertexnummer, X, Y, Z) har værdier.
export function createFullProfile(name = t("Ny profil")){
	const columns = [col("ID", {kind: "rownumber"})];
	for(const f of FEATURE_FIELDS){
		columns.push(col(f.label, {kind: "feature", field: f.field}));
	}
	for(const f of GEOMETRY_FIELDS){
		columns.push(col(f.label, {kind: "geometry", field: f.field}));
	}
	columns.push(col(t("Attributter"), {kind: "attributes-all"}));
	return {
		id: generateProfileId(),
		name,
		outputModel: "vertex",
		separator: ",",
		decimalSeparator: ".",
		encoding: "utf8",
		header: true,
		coordinatePrecision: null,
		columns,
	};
}

// A second, minimal starting point for "Opret ny profil" (task's own
// example name: "Simpel XYZ") - object-mode, unambiguous coordinate fields,
// no attributes. Not persisted/registered anywhere by itself; only used as
// the CsvProfileEditorPanel's "new profile" template.
// (Bevaret til eksisterende kaldere/tests; editorens "Ny profil" bruger createFullProfile.)
export function createBlankProfile(name = t("Ny profil")){
	return {
		id: generateProfileId(),
		name,
		outputModel: "object",
		separator: ",",
		decimalSeparator: ".",
		encoding: "utf8",
		header: true,
		coordinatePrecision: null,
		columns: [
			col("ID", {kind: "feature", field: "id"}),
			col(t("Navn"), {kind: "feature", field: "name"}),
		],
	};
}

export function createColumn(header, source, extra = {}){
	return col(header, source, extra);
}

export function cloneProfile(profile, newId, newName){
	return {
		...profile,
		id: newId || generateProfileId(),
		name: newName || profile.name,
		columns: profile.columns.map((c) => ({...c, id: generateColumnId(), source: {...c.source}})),
	};
}
